import { useState, useEffect, useCallback } from 'react';
import { useLoader, useStore } from '../../../common/hooks';
import { Option, RequestLog, Log } from 'models/log/Log';
import axios from 'axios';
import { format } from 'date-fns';
import Swal from 'sweetalert2';
import {
    getCompanyMasterByUser,
    getUserCompanyByCompany
} from '../../../data/company/repositories/companyMasterRepository';
import { set } from 'mobx';
import { ActionMeta } from 'react-select';
import { MultiValue, SingleValue } from 'react-select';
import * as XLSX from 'xlsx';

const DEFAULT_ACTION: { label: string; value: string }[] = [
    { label: 'Request to Sandbox', value: 'request_to_sandbox' },
    { label: 'Send Email Approve to Sandbox', value: 'send_email_approve_sandbox' },
    { label: 'Add Employee to FocusOne', value: 'add_employee_to_f1' },
    { label: 'Add User to FocusOne', value: 'add_user_to_f1' },
    { label: 'Request to Company', value: 'request_to_company' },
    { label: 'Active User', value: 'active' },
    { label: 'Approve User', value: 'approve' },
    { label: 'Invite User', value: 'invite_user' },
    { label: 'Reject User', value: 'reject' },
    { label: 'Send Email Approve to Company', value: 'send_email_approve' },
    { label: 'Send Email Invite to Company', value: 'send_email_invite' },
    { label: 'Send Email Reject to Company', value: 'send_email_reject' },
    { label: 'Send Email Request to Company', value: 'send_email_request_to_company' }
].map(value => ({
    label: value.label,
    value: value.value
}));

function LogsViewModel() {
    const loader = useLoader();
    const [companyOptions, setCompanyOptions] = useState<Option[]>([]);
    const [userName, setUserName] = useState<string>('');
    const [company, setCompany] = useState<Option | null>(null);
    const [action, setAction] = useState<Option[]>([]);
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [startTime, setStartTime] = useState<string>('');
    const [endTime, setEndTime] = useState<string>('');
    const [logs, setLogs] = useState<Log[]>([]);
    const [userOptions, setUserOptions] = useState<Option[]>([]);

    const { companyMasterStore, userCompanyStore, logStore } = useStore();
    const companyMaster = companyMasterStore.companyMaster;

    const { userRole, citizenId } = userCompanyStore;

    const handleUserNameChange = (newValue: Option | null, actionMeta: ActionMeta<Option>) => {
        if (newValue) {
            setUserName(newValue.value);
        } else {
            setUserName(''); // Set an appropriate fallback value if needed
        }
    };

    const onSelectCompany = (company: SingleValue<Option>) => {
        setCompany(company);
    };

    const toggleAction = (option: Option) => {
        setAction(prev => {
            const exists = prev.some(a => a.value === option.value);
            return exists ? prev.filter(a => a.value !== option.value) : [...prev, option];
        });
    };

    const isAllActionsSelected = action.length === DEFAULT_ACTION.length;

    const toggleAllActions = () => {
        setAction(isAllActionsSelected ? [] : [...DEFAULT_ACTION]);
    };

    useEffect(() => {
        if (!citizenId) return;

        const fetchUserCompanies = async () => {
            const [, data] = await getCompanyMasterByUser({
                citizen_id: citizenId,
                status: 'active'
            });
            if (data?.result_list) {
                console.log(data);
                const options = data.result_list.map(
                    c =>
                        ({
                            label: c.system_type
                                ? `${c.company_name} (${c.system_type})`
                                : c.company_name,
                            value: c.sid
                        }) as Option
                );
                console.log(options);
                setCompanyOptions(options);
            }
        };

        fetchUserCompanies();
        setUserName(citizenId);
    }, [citizenId]);

    useEffect(() => {
        if (!company?.value) {
            setUserOptions([]);
            return;
        }

        const fetchCompanyUsers = async () => {
            const [, users] = await getUserCompanyByCompany({ sid: company.value! });
            if (users && users.length > 0) {
                const options = users.map(
                    u => ({ label: u.citizen_id, value: u.citizen_id }) as Option
                );
                setUserOptions(options);
                setUserName(prev => (options.some(o => o.value === prev) ? prev : ''));
            } else {
                setUserOptions([]);
                setUserName('');
            }
        };

        fetchCompanyUsers();
    }, [company?.value]);

    const resetDateTime = (date: string) => {
        if (date === 'start') {
            setStartDate('');
            setStartTime('');
        }
        if (date === 'end') {
            setEndDate('');
            setEndTime('');
        }
    };

    const handleSearch = async () => {
        try {
            loader.show();

            if (!company?.value) {
                Swal.fire({
                    title: 'Error!',
                    text: 'กรุณาเลือก Company',
                    icon: 'error',
                    showCancelButton: false,
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: 'Yes',
                    allowOutsideClick: false
                });

                return;
            }

            const searchLog: RequestLog = {
                user: userName || undefined,
                sid: company?.value,
                action: action.length > 0 ? action.map(a => a.value) : undefined,
                with: userName || undefined,
                created_on_start:
                    startDate && startTime
                        ? format(new Date(`${startDate}T${startTime}`), "yyyy-MM-dd'T'HH:mm:ssxxx")
                        : '',
                created_on_end:
                    endDate && endTime
                        ? format(new Date(`${endDate}T${endTime}`), "yyyy-MM-dd'T'HH:mm:ssxxx")
                        : ''
            };
            //const logs = (
            //    await axios.post<Log[]>('/v1/GetConditionLogs', searchLog)
            //    //await logStore.fetchLogStore(searchLog)
            //).data;
            console.log(searchLog);
            await logStore.fetchLogStore(searchLog);
            const logsData = logStore.log;

            logsData
                ? logsData.forEach(log => {
                      log.sid = companyMaster.find(company => company.sid === log.sid)
                          ?.company_name;

                      const dateString = log.created_on;
                      const date = new Date(dateString!);
                      const options: Intl.DateTimeFormatOptions = {
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                      };
                      const formatter = new Intl.DateTimeFormat('en-GB', options);
                      const formattedDate = formatter.format(date);

                      log.created_on = formattedDate;
                  })
                : null;

            setLogs(logsData);
        } finally {
            loader.hide();
        }
    };

    const fireSwal = useCallback(
        (title: string, message: string, icon: Parameters<typeof Swal.fire>[2]) => {
            return Swal.fire({
                title,
                text: message,
                icon,
                showCancelButton: false,
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'Yes',
                allowOutsideClick: false
            });
        },
        []
    );

    const exportExcel = () => {
        try {
            loader.show();

            //if (userCompanyMapping.length === 0) {
            //    throw new Error('No user for export.');
            //}

            //// Prepare Data
            //let exportUsers: UserVerifyExportType[] = [];
            //if (filteredUsers.length > 0 && !selectedCompany.includes(DEFAULT_SELECTED_COMPANY)) {
            //    exportUsers = _prepareExportData(filteredUsers);
            //} else {
            //    exportUsers = _prepareExportData(userCompanyMapping);
            //}

            //// Export Excel
            //const date = new Date();
            //const fileName = 'ReportLog_' + date.getTime().toString() + '.xlsx';
            //const worksheet = XLSX.utils.json_to_sheet(exportUsers);
            //const workbook = XLSX.utils.book_new();

            //XLSX.utils.book_append_sheet(workbook, worksheet, 'Report Log');
            //XLSX.writeFile(workbook, fileName);
        } catch (error) {
            if (error instanceof Error) {
                fireSwal('Error!', error.message, 'error');
            }
        } finally {
            loader.hide();
        }
    };

    return {
        DEFAULT_ACTION,
        companyOptions,
        userRole,
        userName,
        setUserName,
        company,
        onSelectCompany,
        action,
        setAction,
        toggleAction,
        isAllActionsSelected,
        toggleAllActions,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        startTime,
        setStartTime,
        endTime,
        setEndTime,
        resetDateTime,
        handleSearch,
        logs,
        userCompanyStore,
        userOptions,
        handleUserNameChange,
        exportExcel
    };
}

export default LogsViewModel;
